import { ContactSyncModel, ContactSyncRunLog, Page } from '../../../../types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getContactSyncRunLogs, UserKeys } from '../../../api/users-api';
import { useMemo, useState } from 'react';
import { GridColDef, Toolbar } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridServer from '../../../shared/shared-data-grid-server';
import AvatarChip from '../../../shared/avatar-chip';
import { Messages } from '../../../utils/message-constants';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import StatusLabel from '../../../shared/status-label';

import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FindInPage from '@mui/icons-material/FindInPage';
import Refresh from '@mui/icons-material/Refresh';

interface ContactSyncRunLogsGridProps {
  selectedContactSync: ContactSyncModel;
}

function DetailPanel({ row }: { row: ContactSyncRunLog }) {
  const [filterInput, setFilterInput] = useState('');
  const [viewType, setViewType] = useState<'synced' | 'failed'>('synced');

  const targetEmails = useMemo(
    () => row.targetEmails ?? [],
    [row.targetEmails]
  );
  const pushedToEmails = useMemo(
    () => row.pushedToEmails ?? [],
    [row.pushedToEmails]
  );
  const pushedSet = useMemo(() => new Set(pushedToEmails), [pushedToEmails]);
  const failedEmails = useMemo(
    () => targetEmails.filter((e) => !pushedSet.has(e)),
    [targetEmails, pushedSet]
  );

  const filteredEmails = useMemo(() => {
    const lower = filterInput.toLowerCase();
    const source = viewType === 'synced' ? pushedToEmails : failedEmails;
    return lower ? source.filter((e) => e.includes(lower)) : source;
  }, [viewType, filterInput, pushedToEmails, failedEmails]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        {row.errorMessage && (
          <Box>
            <Typography variant="subtitle2" color="error">
              Error Details
            </Typography>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                fontSize: 12,
                mt: 0.5,
                maxHeight: 120,
                overflowY: 'auto',
                p: 1,
                bgcolor: 'grey.100',
                borderRadius: 1,
              }}
            >
              {row.errorMessage}
            </Typography>
          </Box>
        )}

        {targetEmails.length > 0 && (
          <>
            <RadioGroup
              row
              value={viewType}
              onChange={(e) =>
                setViewType(e.target.value as 'synced' | 'failed')
              }
            >
              <FormControlLabel
                value="synced"
                control={<Radio size="small" />}
                label={`Synced Users (${pushedToEmails.length})`}
              />
              <FormControlLabel
                value="failed"
                control={<Radio size="small" />}
                label={`Failed Users (${failedEmails.length})`}
              />
            </RadioGroup>

            <Stack flexDirection="row" gap={1} alignItems="center">
              <TextField
                label="Search users"
                size="small"
                sx={{ width: 280 }}
                value={filterInput}
                onChange={(e) => setFilterInput(e.target.value)}
              />
              <Typography variant="body2" color="text.secondary">
                {filteredEmails.length} shown
              </Typography>
            </Stack>

            <Stack
              flexDirection="row"
              flexWrap="wrap"
              gap={1}
              sx={{
                maxHeight: 200,
                overflowY: 'auto',
                p: 0.5,
                alignItems: 'center',
              }}
            >
              {filteredEmails.slice(0, 100).map((email) => (
                <AvatarChip key={email} email={email} type="User" />
              ))}
              {filteredEmails.length > 100 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  + {filteredEmails.length - 100} more
                </Typography>
              )}
              {filteredEmails.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No users match your search.
                </Typography>
              )}
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}

function RunLogsToolbar({ onRefresh }: { onRefresh: () => void }) {
  return (
    <Toolbar>
      <Box sx={{ px: 1, py: 0.5 }}>
        <Tooltip arrow title="Refresh run logs">
          <IconButton size="small" onClick={onRefresh}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Toolbar>
  );
}

export default function ContactSyncRunLogsGrid({
  selectedContactSync,
}: ContactSyncRunLogsGridProps) {
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    cursor: string | null;
  }>({
    pageSize: 50,
    page: 0,
    cursor: null,
  });

  const [cursors, setCursors] = useState<Record<number, string>>({ 0: '' });

  const contactSyncId = selectedContactSync.id;

  const {
    data: runLogs,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [
      UserKeys.ContactSyncRunLogs,
      paginationModel.page,
      paginationModel.pageSize,
      paginationModel.cursor,
      contactSyncId,
    ],
    queryFn: () => {
      const object: Page<ContactSyncRunLog[]> = {
        results: [],
        firstPull: paginationModel.page === 0,
        searchTime: 0,
        pageSize: paginationModel.pageSize,
        cursor: paginationModel.cursor,
        nameSpace: '',
      };
      return getContactSyncRunLogs(object, contactSyncId);
    },
    placeholderData: keepPreviousData,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('contact sync run logs'),
    },
  });

  const toolbarProps = useMemo(() => ({ onRefresh: refetch }), [refetch]);

  const columns: GridColDef<ContactSyncRunLog>[] = useMemo(
    () => [
      {
        field: 'startTime',
        headerName: 'Start Time',
        width: 185,
        type: 'dateTime',
        valueGetter: (value: number) => new Date(value),
        valueFormatter: (params: Date) =>
          dayjs(params).format('MMM D, YYYY, h:mm A'),
      },
      {
        field: 'endTime',
        headerName: 'End Time',
        width: 185,
        type: 'dateTime',
        valueGetter: (value: number | null) =>
          value && value !== 0 ? new Date(value) : null,
        valueFormatter: (params: Date | null) =>
          params ? dayjs(params).format('MMM D, YYYY, h:mm A') : 'Running…',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 140,
        renderCell: ({ value }) => {
          const status =
            value === 'SUCCESS'
              ? 'SUCCESS'
              : value === 'FAILED'
              ? 'FAILED'
              : value === 'PARTIAL_SUCCESS'
              ? 'PARTIAL_SUCCESS'
              : value === 'IN_PROGRESS'
              ? 'INPROGRESS'
              : null;
          return status ? <StatusLabel status={status} /> : null;
        },
      },
      {
        field: 'isManual',
        headerName: 'Manual',
        width: 110,
        type: 'boolean',
      },
      {
        field: 'contactsProcessed',
        headerName: 'Processed',
        description: 'Total number of destination users targeted in this run',
        width: 100,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'contactsSucceeded',
        headerName: 'Succeeded',
        description: 'Users who had contacts successfully synced',
        width: 100,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'contactsFailed',
        headerName: 'Failed',
        description:
          'Users who could not be synced due to an error (e.g. suspended account, contact overflow)',
        width: 80,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
      },
    ],
    []
  );

  return (
    <SharedDataGridServer
      slots={{
        toolbar: useToolbar(RunLogsToolbar, toolbarProps),
        ...createGridOverlays('Run Logs', <FindInPage />),
      }}
      data={runLogs}
      getDetailPanelContent={({ row }) => (
        <DetailPanel key={row.id} row={row} />
      )}
      getDetailPanelHeight={() => 'auto'}
      columns={columns}
      sortingMode="client"
      filterMode="client"
      sx={{
        mt: 1,
        '& .MuiDataGrid-row': { cursor: 'default' },
      }}
      pageSizeOptions={[25, 50, 100]}
      initialState={{
        pagination: {
          paginationModel: { page: 0, pageSize: 50 },
          rowCount: 0,
        },
      }}
      paginationModel={{
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      }}
      onPaginationModelChange={(newModel) => {
        if (isLoading || isFetching) return;

        if (newModel.pageSize !== paginationModel.pageSize) {
          setCursors({ 0: '' });
          setPaginationModel((prev) => ({
            ...prev,
            page: 0,
            pageSize: newModel.pageSize,
            cursor: null,
          }));
        } else {
          const isForward = newModel.page > paginationModel.page;
          const nextCursor = runLogs?.cursor || '';

          if (isForward) {
            setCursors((prev) => ({
              ...prev,
              [newModel.page]: nextCursor,
            }));
          }

          setPaginationModel((prev) => ({
            ...prev,
            page: newModel.page,
            pageSize: newModel.pageSize,
            cursor: isForward ? nextCursor : cursors[newModel.page] || null,
          }));
        }
      }}
      loading={isLoading || isFetching}
      disableColumnSelector
      disableRowSelectionOnClick
      getRowId={(row) => row.id}
    />
  );
}
